/**
 * Segment / session GST address helpers.
 * Same sources as buildBannerGstInfo (orderConfirmationUtils):
 *  - billing  ← cart.gstDetails
 *  - shipping ← cart.deliveryInfo.deliveryAddress (or cart.deliveryAddress)
 *  - also gstDetails.shipToGstNumber (getGstShippingAddress parity)
 * No localStorage / client-only state.
 */

const pinOf = value => {
  if (value === undefined || value === null || String(value).trim() === '') return '';
  return String(value);
};

/** Same entity shape as buildBannerGstInfo(cart). */
const getBannerGstSources = (cart = {}) => {
  const gstDetails = cart?.gstDetails || {};
  const gstInfoMessage = cart?.gstInfoMessage || {};
  const deliveryAddress =
    cart?.deliveryInfo?.deliveryAddress || cart?.deliveryAddress || {};
  const billingAddr = gstDetails?.address || {};

  const billingGstNumber = gstDetails?.gstNumber || '';
  const shippingGstNumber =
    deliveryAddress?.gstNumber || gstDetails?.shipToGstNumber || '';

  const billingAddressPincode = pinOf(billingAddr?.postalCode || billingAddr?.pinCode);
  const shippingAddressPincode = pinOf(
    deliveryAddress?.postalCode || deliveryAddress?.pinCode || billingAddressPincode
  );

  return {
    gstDetails,
    gstInfoMessage,
    deliveryAddress,
    billingGstNumber,
    shippingGstNumber,
    billingAddressPincode,
    shippingAddressPincode,
    hasGstSignal: Boolean(
      billingGstNumber || shippingGstNumber || gstInfoMessage?.value
    )
  };
};

/**
 * Segment GST address props for Proceed / Payment.
 */
export const getGstAddressSegmentPropsFromCart = (cart = {}) => {
  const sources = getBannerGstSources(cart);
  if (!sources.hasGstSignal) return {};

  const {
    billingGstNumber,
    shippingGstNumber,
    billingAddressPincode,
    shippingAddressPincode,
    gstDetails
  } = sources;

  const billingGstApplicable = Boolean(billingGstNumber);
  // same as buildBannerGstInfo shippingAddress.gstNumber / getGstShippingAddress
  const shippingGstApplicable = Boolean(shippingGstNumber);

  let sameBillingShipping = billingAddressPincode === shippingAddressPincode;
  if (gstDetails?.sameAsBillingAddress === true) sameBillingShipping = true;
  if (gstDetails?.sameAsBillingAddress === false) sameBillingShipping = false;

  return {
    ...(billingAddressPincode && {
      billing_address_pincode: billingAddressPincode
    }),
    ...(shippingAddressPincode && {
      shipping_address_pincode: shippingAddressPincode
    }),
    ...(billingAddressPincode &&
      shippingAddressPincode && {
        same_billing_shipping_address: sameBillingShipping
      }),
    ...(billingGstApplicable && {
      billing_address_gst_applicable: true,
      shipping_address_gst_applicable: shippingGstApplicable
    })
  };
};

/**
 * sessionStorage gstInfo for Purchased / pe-ui Pay Now.
 */
export const buildGstInfoSessionPayload = (cart = {}, cartItem = [], enableGST = false) => {
  const sources = getBannerGstSources(cart);

  return {
    enableGST,
    gstDetails: sources.gstDetails,
    cartItemGST: (cartItem || []).map(x => ({
      code: x.code,
      isGstEligible: x.isGstEligible
    })),
    ...(sources.billingAddressPincode && {
      selectedBillingAddress: {
        pinCode: sources.billingAddressPincode,
        gstNumber: sources.billingGstNumber
      }
    }),
    ...(sources.shippingAddressPincode && {
      selectedShippingAddress: {
        pinCode: sources.shippingAddressPincode,
        gstNumber: sources.shippingGstNumber
      }
    })
  };
};

export const getGstSessionAddressPins = (cart = {}, gstDetailsOverride) => {
  const sources = getBannerGstSources({
    ...cart,
    gstDetails: gstDetailsOverride || cart?.gstDetails
  });
  return {
    ...(sources.billingAddressPincode && {
      selectedBillingAddress: { pinCode: sources.billingAddressPincode }
    }),
    ...(sources.shippingAddressPincode && {
      selectedShippingAddress: { pinCode: sources.shippingAddressPincode }
    })
  };
};

/** Shipping GST applicable — same rule as buildBannerGstInfo shipping gstNumber. */
export const isShippingGstApplicableFromGstInfo = (gstInfo = {}) => {
  return Boolean(
    gstInfo?.selectedShippingAddress?.gstNumber ||
      gstInfo?.gstDetails?.shipToGstNumber ||
      gstInfo?.gstDetails?.shippingAddress?.gstNumber
  );
};
